// SEO helpers — defaults, JSON-LD builders, slug + auto-link utilities.

export const PAGE_SEO_DEFAULTS = {
  home: {
    title: "Royal Skin Clinic Dubai | Luxury Aesthetic Clinic in DHCC",
    description:
      "Doctor-led skin, hair & facial aesthetics in Dubai Healthcare City. BB Laser, HydraFacial, microneedling, exosomes & more. Book your consultation today.",
  },
  treatments: {
    title: "Skin, Hair & Body Treatments | Royal Skin Clinic Dubai",
    description:
      "Explore physician-led treatments: BB Laser, Carbon Laser Peel, HydraFacial, microneedling, skin boosters and wellness programs in Dubai Healthcare City.",
  },
  doctors: {
    title: "Dr. Javeria Atif, Aesthetic Physician | Royal Skin Clinic",
    description:
      "Dr. Javeria Atif leads every treatment at Royal Skin Clinic Dubai — personalised, doctor-led aesthetic medicine with natural-looking results.",
  },
  about: {
    title: "About | Where Science Meets Aesthetics | Royal Skin",
    description:
      "A doctor-led luxury aesthetic clinic in Dubai Healthcare City — medical-grade technology, honest guidance and an atmosphere of absolute calm.",
  },
  gallery: {
    title: "Before & After Results | Royal Skin Clinic Dubai",
    description:
      "Unretouched before-and-after outcomes from Royal Skin Clinic Dubai — skin rejuvenation, pigmentation, anti-aging and hair restoration results.",
  },
  blog: {
    title: "The Skin Journal | Expert Advice | Royal Skin Clinic",
    description:
      "Weekly physician-written guidance on skin, hair and aesthetic treatments — evidence-based advice from Royal Skin Clinic, Dubai Healthcare City.",
  },
  contact: {
    title: "Book a Consultation | Royal Skin Clinic Dubai — DHCC",
    description:
      "Book your consultation at Royal Skin Clinic, Al-D91 Riyadh Street, Dubai Healthcare City. Call +971 4 276 9797 — we reply within hours.",
  },
};

export const slugify = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/\b(and|the|of|for|in|on|at|to|a|an|with|your)\b/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

export const medicalClinicSchema = (s, origin) => ({
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  name: s.clinic_name || "Royal Skin Cosmetic Clinic",
  description: s.tagline || undefined,
  url: origin,
  telephone: s.phone || undefined,
  email: s.email || undefined,
  image: s.brand_lockup_url ? origin + s.brand_lockup_url : undefined,
  address: s.address
    ? { "@type": "PostalAddress", streetAddress: s.address, addressLocality: "Dubai", addressRegion: "Dubai", addressCountry: "AE" }
    : undefined,
  geo: { "@type": "GeoCoordinates", latitude: 25.2297, longitude: 55.3209 },
  areaServed: { "@type": "City", name: "Dubai" },
  medicalSpecialty: ["Dermatology", "Aesthetic Medicine"],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "12:00",
      closes: "20:00",
    },
  ],
  sameAs: [s.instagram].filter(Boolean),
  priceRange: "AED 350 - AED 2,500",
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export const articleSchema = (a, s, origin) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: a.seo_title || a.title,
  description: a.seo_description || a.excerpt,
  image: a.image ? (a.image.startsWith("http") ? a.image : origin + a.image) : undefined,
  author: { "@type": "Person", name: a.author || "Dr. Javeria Atif" },
  publisher: {
    "@type": "Organization",
    name: s.clinic_name || "Royal Skin Cosmetic Clinic",
    logo: s.brand_lockup_url ? { "@type": "ImageObject", url: origin + s.brand_lockup_url } : undefined,
  },
  datePublished: a.published_at || undefined,
  mainEntityOfPage: `${origin}/blog/${a.slug || a.id}`,
});

export const breadcrumbSchema = (items, origin) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.path ? origin + it.path : undefined,
  })),
});

// Contextual auto-linking: first match per dictionary entry, word-boundary,
// case-insensitive, max `maxLinks` per article. Plain-text paragraphs only,
// so headings are excluded by construction.
export const linkifyParagraph = (text, dictionary, state, currentPath) => {
  if (!dictionary?.length) return [text];
  const nodes = [];
  let rest = text;
  let key = 0;
  // longest phrases first so "skin clinic dubai" wins over "skin clinic"
  const entries = [];
  dictionary.forEach((d) => {
    (d.keyword_list || []).forEach((phrase) => {
      const p = (phrase || "").trim();
      if (p && d.target_url && d.target_url !== currentPath) entries.push({ phrase: p, url: d.target_url });
    });
  });
  entries.sort((a, b) => b.phrase.length - a.phrase.length);

  while (rest && state.count < 5) {
    let earliest = null;
    for (const e of entries) {
      if (state.linked.has(e.url + e.phrase.toLowerCase())) continue;
      const re = new RegExp(`\\b${e.phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
      const m = rest.match(re);
      if (m && (earliest === null || m.index < earliest.index)) {
        earliest = { index: m.index, text: m[0], entry: e };
      }
    }
    if (!earliest) break;
    const before = rest.slice(0, earliest.index);
    if (before) nodes.push(before);
    nodes.push({ link: earliest.text, url: earliest.entry.url, key: key++ });
    state.linked.add(earliest.entry.url + earliest.entry.phrase.toLowerCase());
    state.count += 1;
    rest = rest.slice(earliest.index + earliest.text.length);
  }
  if (rest) nodes.push(rest);
  return nodes;
};

// Long-form agent articles carry subheadings as blocks prefixed with "## ".
export const isHeadingBlock = (p) => typeof p === "string" && p.startsWith("## ");
export const headingText = (p) => (p || "").slice(2).trim();
